import { useEffect, useRef, useState } from 'react'

interface AnimatedNumberProps {
  value: number
  /** Total animation duration in ms. Default 700. */
  duration?: number
  /** Format the displayed number — default formats with locale separators */
  format?: (n: number) => string
  className?: string
}

/**
 * Counts up to `value` from 0 (or the previous value) on mount and on change.
 * Uses requestAnimationFrame with an ease-out curve so the count *decelerates*
 * toward the final number — feels more intentional than linear.
 *
 * Honors prefers-reduced-motion by snapping straight to the value.
 */
export function AnimatedNumber({ value, duration = 700, format, className }: AnimatedNumberProps) {
  const [display, setDisplay] = useState(value)
  const fromRef = useRef(value)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      setDisplay(value)
      fromRef.current = value
      return
    }
    const from = fromRef.current
    const delta = value - from
    if (delta === 0) return
    const start = performance.now()
    let raf: number

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      // ease-out cubic — feels like Emil's --ease-out curve
      const eased = 1 - Math.pow(1 - t, 3)
      const next = from + delta * eased
      setDisplay(Number.isInteger(value) ? Math.round(next) : Number(next.toFixed(2)))
      if (t < 1) raf = requestAnimationFrame(tick)
      else fromRef.current = value
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, duration])

  return <span className={className}>{format ? format(display) : display.toLocaleString()}</span>
}
